import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderGetTagProps,
  Chip,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { omit, isEmpty } from 'lodash-es';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ILocation,
  getMonsterLocations,
  getZipLocations,
  getCVLibraryLocations,
  getJobSiteLocations,
} from 'wisestep-api';
import { Label, WsSelect, WsSelectObj, ControlledTextInput } from 'wisestep-base';

interface MonsterLocationType {
  id: string;
  label: string;
}

interface CVLibraryLocationType {
  id: string;
  country: string;
  city: string;
  district: string;
  apiParameter: string;
  location: string;
}

const distanceOptions: WsSelectObj[] = [
  {
    label: '20 mi',
    value: 20,
  },
  {
    label: '30 mi',
    value: 30,
  },
  {
    label: '50 mi',
    value: 50,
  },
  {
    label: '100 mi',
    value: 100,
  },
  {
    label: '200 mi',
    value: 200,
  },
  {
    label: '500 mi',
    value: 500,
  },
  {
    label: '1000 mi',
    value: 1000,
  },
];

const mapLocationSize = new Map();
mapLocationSize.set(true, 9);
mapLocationSize.set(false, 12);

export function WsLocationAndZipSearch({
  required = false,
  placeHolder = 'Search Location',
  size = 'small',
  multiSelect = true,
  showDistance = false,
  distanceSelectName = 'distance',
  showMandatorySymbol = false,
  variant = 'outlined',
  name,
  hideLabel = false,
  disabled = false,
  selectedCountry = '',
  searchCriteria = 'zip',
  account = '',
  jobBoard = 'MONSTER',
}: ILocationAndZipSearchProps) {
  const [options, setOptions] = React.useState<MonsterLocationType[]>([]);

  // const [showCountry, setShowCountry] = React.useState(true);

  const { control, setValue, getValues } = useFormContext();
  const defaultValue = getValues(name);
  if (defaultValue == null) {
    setValue(name, multiSelect ? [] : null);
  }
  let cvLibraryResponse: CVLibraryLocationType[] = [];
  let jobSiteAndCWJobListResponse: string[] = [];
  React.useEffect(() => {
    // if (searchCriteria === 'location') {
    //   setValue('selectedCountry', null);
    //   setShowCountry(false);
    // }

    // if (searchCriteria === 'zip' && account) {
    //   const country = (account.substring(account.indexOf('(')).split('_')[1] ?? '')
    //     .replace(')', '')
    //     .trim();
    //   setShowCountry(country === 'all');
    // }
    setOptions([]);
    setValue(name, []);
  }, [account, selectedCountry, searchCriteria, name, setValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    fetchMonsterLocations(event.target.value);
  };

  const fetchMonsterLocations = async (input: string) => {
    switch (jobBoard) {
      case 'MONSTER':
        {
          const country = (account.substring(account.indexOf('(')).split('_')[1] ?? '')
            .replace(')', '')
            .trim();
          if (searchCriteria === 'location' || searchCriteria === 'country') {
            const data = await getMonsterLocations(
              input,
              country && country != 'all' ? country.toUpperCase() : '',
              searchCriteria
            );
            setOptions(data.data ?? []);
          } else {
            const data = await getZipLocations(input);
            setOptions(data.data ?? []);
          }
        }
        break;
      case 'CV_LIBRARY':
        {
          const apiMethod = getCVLibraryLocations;
          const data = await apiMethod(input);
          cvLibraryResponse = data.data ?? [];

          if (!isEmpty(cvLibraryResponse)) {
            cvLibraryResponse.map((cvLibraryResponse) =>
              options.push({
                id: cvLibraryResponse.apiParameter,
                label: cvLibraryResponse.location,
              })
            );
          }
          setOptions(options);
        }
        break;
      case 'CW_JOBS':
        {
          jobSiteAndCWResponseMap(input);
        }
        break;
      case 'JOB_SITE':
        {
          jobSiteAndCWResponseMap(input);
        }
        break;
    }
  };

  const jobSiteAndCWResponseMap = async (input: string) => {
    const apiMethod = getJobSiteLocations;
    const data = await apiMethod(input);
    jobSiteAndCWJobListResponse = data.data ?? [];
    const newOptions: MonsterLocationType[] = [];

    if (!isEmpty(jobSiteAndCWJobListResponse)) {
      jobSiteAndCWJobListResponse.map((jobSiteAndCWLocation) =>
        newOptions.push({
          id: jobSiteAndCWLocation,
          label: jobSiteAndCWLocation,
        })
      );
    }
    setOptions(newOptions);
  };
  const onChange = (
    locationsData: MonsterLocationType[],
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails
  ) => {
    if (reason === 'clear') {
      multiSelect ? setValue(name, []) : setValue(name, null);
    } else if (reason === 'removeOption') {
      removeOption(details);
    } else {
      setLocations(locationsData);
    }
  };

  const removeOption = (details: AutocompleteChangeDetails) => {
    const selectedLocations = getValues(name) ?? [];
    const index = selectedLocations.indexOf(details.option);
    selectedLocations.splice(index, 1);
    setValue(name, selectedLocations);
  };

  const isEqualValueCheck = (option, value) => {
    const prop =
      searchCriteria === 'location' ||
      searchCriteria === 'country' ||
      jobBoard === 'CV_LIBRARY' ||
      jobBoard === 'CW_JOBS' ||
      jobBoard === 'JOB_SITE'
        ? 'id'
        : 'zipCode';

    return option[prop] === value[prop];
  };

  const setLocations = (locationsData: MonsterLocationType[]) => {
    setValue(name, locationsData);
  };

  const getOptionTags = (
    data: (ILocation & MonsterLocationType)[],
    tagsProps: AutocompleteRenderGetTagProps
  ) =>
    data.map((value, index) => {
      const { onDelete } = tagsProps({ index });
      return (
        <Chip
          key={index}
          sx={{
            margin: 0.1,
            padding: 0.1,
          }}
          label={value?.label ?? value}
          onDelete={onDelete}
        />
      );
    });
  return (
    <Grid container spacing={2} display="flex" flexDirection="row">
      <Grid item xs={searchCriteria === 'country' ? 6 : mapLocationSize.get(showDistance)}>
        {!hideLabel && (
          <Label>
            {placeHolder}
            {showMandatorySymbol ? ' *' : ''}
          </Label>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field: { value }, fieldState: { error, invalid } }) => (
            <Autocomplete
              disabled={disabled}
              freeSolo={searchCriteria === 'zip'}
              autoSelect
              id="google-map-demo"
              size={size}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
              filterOptions={(x) => x}
              options={options}
              autoComplete
              fullWidth
              multiple={multiSelect}
              includeInputInList
              isOptionEqualToValue={isEqualValueCheck}
              renderTags={(data, tagsProps) => getOptionTags(data, tagsProps)}
              value={value}
              onChange={(
                event: React.ChangeEvent<any>,
                value: any,
                reason: AutocompleteChangeReason,
                details: any
              ) => onChange(value, reason, details)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeHolder}
                  variant={variant}
                  fullWidth
                  onChange={(event: React.ChangeEvent<any>) => handleChange(event)}
                  required={required}
                  {...omit(params, ['label'])}
                  error={invalid}
                  helperText={error?.message}
                />
              )}
            />
          )}
        />
      </Grid>
      {jobBoard === 'MONSTER' && searchCriteria === 'country' && (
        <Grid item xs={3}>
          <ControlledTextInput
            name={'zipcode'}
            fullWidth
            id={`zipcode-field`}
            placeholder={'eg 1234'}
            variant="outlined"
            size="small"
            labelText="Zip Code"
            inputProps={{
              style: {
                padding: '9px 14px',
              },
            }}
          />
        </Grid>
      )}
      {(showDistance || searchCriteria === 'country') && (
        <Grid item xs={3}>
          <WsSelect
            name={distanceSelectName}
            selectArray={distanceOptions}
            label="Radius"
            size="small"
            defaultValue="50"
          />
        </Grid>
      )}
    </Grid>
  );
}
export default WsLocationAndZipSearch;
export interface ILocationAndZipSearchProps {
  required?: boolean;
  name: string;
  placeHolder: string;
  size?: 'small' | 'medium';
  multiSelect?: boolean;
  showDistance?: boolean;
  distanceSelectName?: string;
  showMandatorySymbol?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  hideLabel?: boolean;
  disabled?: boolean;
  searchCriteria: string;
  selectedCountry?: string;
  account?: string;
  jobBoard: string;
}
